import { useEffect, type ReactNode } from 'react';
import clsx from 'clsx';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  side?: 'left' | 'right';
  /** Tailwind width classes for the panel. */
  widthClass?: string;
  label?: string;
  /** Stacking context; defaults above TopBar (z-[9999]) and map UI. */
  zClass?: string;
}

/**
 * Off-canvas overlay drawer (mobile sidebar / side panels).
 * Backdrop tap + Escape close it; body scroll is locked while open.
 * Transform-based slide keeps the animation on the compositor.
 */
export default function Drawer({
  open,
  onClose,
  children,
  side = 'left',
  widthClass = 'w-72 max-w-[85vw]',
  label = 'Drawer',
  zClass = 'z-[10500]',
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div
      className={clsx('fixed inset-0', zClass, !open && 'pointer-events-none')}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        className={clsx(
          'absolute inset-0 bg-black/60 transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-label={`Close ${label}`}
        role="button"
        tabIndex={-1}
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={clsx(
          'absolute top-0 bottom-0 flex flex-col bg-command-panel shadow-2xl',
          'transition-transform duration-300 ease-out will-change-transform',
          widthClass,
          side === 'left'
            ? ['left-0 border-r border-command-border', open ? 'translate-x-0' : '-translate-x-full']
            : ['right-0 border-l border-command-border', open ? 'translate-x-0' : 'translate-x-full']
        )}
      >
        {children}
      </div>
    </div>
  );
}
